/**
 * @file Post-traitement optionnel : bloom leger (tirs, impacts, objets brillants) via EffectComposer, et vignette en surcouche CSS
 * (aucun cout GPU). Le bloom n'existe qu'en qualite « haute » hors mode eco ; sinon on rend directement (chemin historique).
 */
import { HalfFloatType, Vector2, WebGLRenderTarget } from 'three';
import type { Camera, Scene, WebGLRenderer } from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { RENDER } from '@config/index';

export class PostFx {
  private composer: EffectComposer | null = null;
  private bloom: UnrealBloomPass | null = null;
  private enabled = false;
  private readonly size = new Vector2(1, 1);
  private vignette: HTMLDivElement | null = null;

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly scene: Scene,
    private readonly camera: Camera,
    canvas: HTMLCanvasElement,
  ) {
    this.mountVignette(canvas);
  }

  /** Active / coupe le bloom (qualite haute et pas eco). Le composer est cree a la premiere activation puis conserve. */
  setEnabled(on: boolean): void {
    this.enabled = on && RENDER.post.bloom.enabled;
    if (this.enabled && !this.composer) this.build();
  }

  get active(): boolean {
    return this.enabled;
  }

  setSize(cssW: number, cssH: number, dpr: number): void {
    this.size.set(cssW, cssH);
    if (!this.composer) return;
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(cssW, cssH);
    this.bloom?.resolution.set(cssW * RENDER.post.bloom.resolutionScale, cssH * RENDER.post.bloom.resolutionScale);
  }

  render(): void {
    if (this.enabled && this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  private build(): void {
    const b = RENDER.post.bloom;
    // MSAA sur la cible du composer (WebGL2) : sinon l'antialias du canvas est perdu des qu'on passe par le post-traitement
    const target = new WebGLRenderTarget(1, 1, { type: HalfFloatType, samples: this.renderer.capabilities.isWebGL2 ? 4 : 0 });
    const composer = new EffectComposer(this.renderer, target);
    composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloom = new UnrealBloomPass(new Vector2(this.size.x * b.resolutionScale, this.size.y * b.resolutionScale), b.strength, b.radius, b.threshold);
    composer.addPass(this.bloom);
    composer.addPass(new OutputPass());
    this.composer = composer;
    this.setSize(this.size.x, this.size.y, this.renderer.getPixelRatio());
  }

  /** Vignette : simple degrade radial CSS par-dessus le canvas (compositeur du navigateur, gratuit cote GPU du jeu). */
  private mountVignette(canvas: HTMLCanvasElement): void {
    const v = RENDER.post.vignette;
    const parent = canvas.parentElement;
    if (!v.enabled || !parent || typeof document === 'undefined') return;
    const el = document.createElement('div');
    el.className = 'game-vignette';
    el.setAttribute('aria-hidden', 'true');
    el.style.cssText = `position:absolute;inset:0;pointer-events:none;z-index:0;background:radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) ${Math.round(v.inner * 100)}%, ${v.color} 100%);opacity:${v.opacity};`;
    canvas.insertAdjacentElement('afterend', el);
    this.vignette = el;
  }

  dispose(): void {
    this.composer?.dispose();
    this.bloom?.dispose();
    this.composer = null;
    this.vignette?.remove();
    this.vignette = null;
  }
}
